import { Component } from '@theme/component';
import { DrawerCloseEvent, DrawerOpenEvent } from '@theme/theme-drawer';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {Set<EventTarget>} */
  #scrollTargets = new Set();

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /** @type {number} */
  #upwardDistance = 0;

  /** @type {number} */
  #downwardDistance = 0;

  /** @type {number | null} */
  #initialSyncTimer = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        const nextOffscreen = !isIntersecting || this.dataset.stickyState === 'active';

        if (nextOffscreen && !this.#offscreen) {
          this.#lastScrollTop = getScrollTop();
        }

        this.#offscreen = nextOffscreen;
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    this.#bindScrollListeners();
    this.#resetScrollTracking();

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
    this.#queueInitialStateSync();
  };

  #bindScrollListeners = () => {
    this.#scrollTargets.forEach((target) => target.removeEventListener('scroll', this.#handleWindowScroll));
    this.#scrollTargets.clear();

    const targets = [
      getScrollEventTarget(),
      window,
      document,
      document.querySelector('.page-wrapper'),
    ];

    targets.forEach((target) => {
      if (!target || this.#scrollTargets.has(target)) return;
      target.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      this.#scrollTargets.add(target);
    });
  };

  #resetScrollTracking = () => {
    this.#lastScrollTop = getScrollTop();
    this.#upwardDistance = 0;
    this.#downwardDistance = 0;
  };

  #syncStickyState = (showWhenRestored = false) => {
    if (!this.isConnected) return;

    const stickyMode = this.getAttribute('sticky');
    const scrollTop = getScrollTop();
    this.#resetScrollTracking();

    if (stickyMode !== 'scroll-up') return;

    if (scrollTop <= 2) {
      this.#offscreen = false;
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
      return;
    }

    if (showWhenRestored) {
      this.#offscreen = true;
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'up';
    }
  };

  #queueInitialStateSync = () => {
    this.#syncStickyState(true);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#syncStickyState(true));
    });

    if (this.#initialSyncTimer !== null) clearTimeout(this.#initialSyncTimer);
    this.#initialSyncTimer = window.setTimeout(() => {
      this.#initialSyncTimer = null;
      this.#syncStickyState(true);
    }, 180);
  };

  #handlePageShow = () => {
    this.#queueInitialStateSync();
  };

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#queueInitialStateSync();
  };

  #syncCartDrawerState = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    this.classList.toggle('cart-drawer-header-filled', Boolean(cartDrawer?.hasAttribute('open')));
  };

  /**
   * Keeps transparent headers visually filled while the cart drawer is open.
   * @param {Event} event
   */
  #handleDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartDrawerState();
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = getScrollTop();
    const scrollDelta = scrollTop - this.#lastScrollTop;
    const isAtTop = scrollTop <= 2;
    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    const revealDistance = isMobile ? 2 : 1;

    this.#lastScrollTop = scrollTop;

    if (stickyMode !== 'always' && isAtTop) {
      this.#offscreen = false;
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
      this.#upwardDistance = 0;
      this.#downwardDistance = 0;
      return;
    }

    if (Math.abs(scrollDelta) < 0.5) return;

    const isScrollingUp = scrollDelta < 0;

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      return;
    }

    if (isScrollingUp) {
      this.#upwardDistance += Math.abs(scrollDelta);
      this.#downwardDistance = 0;

      if (this.#upwardDistance >= revealDistance) {
        this.#offscreen = true;
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
      return;
    }

    this.#downwardDistance += scrollDelta;
    this.#upwardDistance = 0;

    if (this.#downwardDistance < 18) return;

    this.dataset.scrollDirection = 'none';
    this.dataset.stickyState = 'idle';
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerStateChange);
    this.#syncCartDrawerState();
    requestAnimationFrame(() => this.#syncCartDrawerState());

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#bindScrollListeners();
        this.#resetScrollTracking();
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
      window.addEventListener('pageshow', this.#handlePageShow);
      window.addEventListener('load', this.#handlePageShow);
      document.addEventListener('visibilitychange', this.#handleVisibilityChange);
      this.#queueInitialStateSync();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerStateChange);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('load', this.#handlePageShow);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#scrollTargets.forEach((target) => target.removeEventListener('scroll', this.#handleWindowScroll));
    this.#scrollTargets.clear();
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#initialSyncTimer !== null) {
      clearTimeout(this.#initialSyncTimer);
      this.#initialSyncTimer = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
